/**
 * Learn Mode — the lessons.
 *
 * Every step names a REAL route and a REAL control (`data-tour` key). The tour
 * navigates there, dims everything except that control, and waits for the agent
 * to actually operate it. Nothing here draws a screen: the screens are the app's.
 *
 * Step shape
 *   route          where the step happens; the tour navigates there
 *   anchor         data-tour key of the control to spotlight
 *   advanceOnTap   the agent must tap the anchor
 *   advanceWhen    (state) => bool — wait for the SANDBOX to reach a state.
 *                  Stronger than a tap, and the only correct test when a tap
 *                  merely opens a sheet: tapping "Change" opens the stage picker,
 *                  it does not change the stage.
 *   actor          AGENT (default) | HQ | PEER — picks the coach bar's skin
 *   beat           fixture mutation applied when a simulated step is played
 *   toast          real server notification copy, verbatim
 *
 *   navState      router state the target screen needs to be meaningful. May be a
 *                  (path) => state function when the same screen is reached two
 *                  ways — /forms/new is a different screen with and without a
 *                  listing behind it.
 *   path          'listing' | 'direct' — the step belongs to ONE branch of the
 *                  fork. Untagged steps are on both. See PICK_PATH.
 *   choices       turns the step into the fork itself: the coach bar offers these
 *                  instead of Next, and picking one selects the branch.
 *
 * EVERY STEP SHOULD CARRY A `route`. A step without one cannot be navigated to,
 * so Back lands on it and changes the coach copy while the screen stays put —
 * which reads as the tour being stuck. FormCreatePage needs the listing handed
 * over in router state, so those steps carry `...AT_FORM` rather than going
 * without. The tour skips navigation when it is already on the target path, so
 * moving between steps on one screen never remounts a half-filled form.
 */
import { DEMO_IDS } from './fixtures';
import { toDesktop } from './tourDesktop';

/**
 * The label the FORM is currently showing for a field, quoted verbatim. The
 * quick-create form relabels itself — "Full name (as per IC)" vs
 * "Name (as per SSM)", "IC Number" vs "SSM Registration No" vs "Passport Number"
 * — so a step that hardcoded "IC" would contradict the very screen it points at.
 * Reading the live label keeps the lesson honest whatever the agent picked.
 */
const labelFor = (key) => {
  const el = document.querySelector(`[data-tour="${key}"]`);
  if (!el) return '';
  // The label that visually belongs to a field is the LAST one before it in
  // document order. Climbing the tree instead is brittle here — AntD wraps some
  // inputs, so a fixed number of hops lands on a different field's label.
  const labels = [...document.querySelectorAll('label')].filter(
    (l) => l.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
  );
  // Quoted verbatim, case and all — lower-casing turns "SSM Registration No"
  // into "ssm registration no", which reads as a typo rather than a field name.
  const txt = labels.length ? labels[labels.length - 1].textContent : '';
  return txt.replace(/^\*\s*/, '').trim();
};

const L = DEMO_IDS;

/**
 * The router state the listing screen hands to /forms/new. Reproduced here so the
 * create screen is reachable directly — by Back, and by resuming a saved lesson —
 * and not only by tapping forward from the listing. Without it /forms/new renders
 * a blank, unlinked form and the step points at nothing.
 */
const FROM_LISTING = {
  fromListing: {
    id: L.listing,
    listing_ref: 'PL-DEMO-01',
    listing_type: 'SUBSALE',
    property_title: 'Residensi Amanina A-12-03 (demo)',
    property_address: 'A-12-03, Residensi Amanina, Jalan Amanina 3',
    asking_price_rm: 680000,
    owner_name: null,
    owner_phone: null,
    area: 'Petaling Jaya',
    state: 'Selangor',
    property_type: 'Condominium',
    is_mine: true,
    agent_name: null,
  },
  fromAuthForm: null,
};
// `block` marks a run of steps that can only be done from its beginning. Backing
// into the middle of one would show a screen whose state no longer exists —
// /forms/new is always a fresh form — so the tour rewinds to the block's first
// step and resets the sandbox to match. See TourContext.back().
// A form written from scratch must NOT be handed the listing state — that is the
// whole difference between the two branches, and Back or a resume would otherwise
// quietly turn the from-scratch form into a from-listing one.
const AT_FORM = {
  route: '/forms/new',
  navState: (path) => (path === 'direct' ? undefined : FROM_LISTING),
  block: 'form-create',
};
// The form's own screens. The signing tab is in the URL (see FormDetailPage), so
// a step there is reachable directly rather than only by tapping the tab.
const AT_OTP = { route: `/forms/${L.otp}` };
const AT_OTP_SIGNING = { route: `/forms/${L.otp}?tab=signing`, block: 'form-sign' };
const AT_SALE = { route: `/sales/${L.sale}` };
// The stage picker is local state on the sale page — a fresh mount always has the
// sheet shut, so these steps are only doable from the one that opens it.
const AT_STAGE = { route: `/sales/${L.sale}`, block: 'stage-sheet' };

/* ═══ LESSON 1 · the whole deal ═══════════════════════════════════════════ */
const SPINE = [
  {
    id: 'home',
    chapter: 'THE HOUSE', route: '/',
    say: 'This is your home screen. Nothing in this lesson is real.',
    do: 'We made up a house, an owner and a buyer for you to practise on.',
  },
  /* ── the fork ──────────────────────────────────────────────────────────────
     Not every offer starts from a listing. Plenty of deals arrive the other way
     round — a buyer is ready and the paper is written on the spot — so the lesson
     asks which one to practise rather than teaching only the tidy route. The two
     branches differ for exactly five steps and then rejoin at the buyer. */
  {
    id: 'pick-path',
    chapter: 'THE HOUSE', route: '/',
    say: 'There are two ways to write an offer. Which one shall we practise?',
    do: 'Both end up at the same form. Pick either — you can come back and try the other.',
    choices: [
      { key: 'listing', label: 'From my listing', hint: 'the property fills itself in, and cannot be mistyped' },
      { key: 'direct', label: 'Straight to a form', hint: 'nothing behind it — you type the property yourself' },
    ],
  },

  /* ── branch A · from the listing ── */
  {
    id: 'tab-listings',
    path: 'listing',
    chapter: 'THE HOUSE', route: '/', anchor: 'tab-listings', advanceOnTap: true,
    say: 'Your properties live in Listings.',
    do: 'Tap Listings at the bottom.',
  },
  {
    id: 'listing-card',
    path: 'listing',
    chapter: 'THE HOUSE', route: '/listings', anchor: 'listings-card', advanceOnTap: true,
    say: 'Here is the practice house.',
    do: 'Tap the card to open it.',
  },
  {
    id: 'visibility',
    path: 'listing',
    chapter: 'THE HOUSE', route: `/listings/${L.listing}`, anchor: 'listing-visibility', advanceOnTap: true,
    say: 'Choose who else can see it.',
    do: 'Internal is only you. Public lets other CID agents ask to help sell it. Tap either one.',
  },
  {
    id: 'generate-form',
    path: 'listing',
    chapter: 'THE PAPER', route: `/listings/${L.listing}`, anchor: 'listing-primary-action', advanceOnTap: true,
    say: 'A buyer wants it, so now you write the offer.',
    do: 'Tap Generate booking form.',
  },
  {
    id: 'form-prefilled',
    path: 'listing',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-from-listing-banner',
    say: 'The property is filled in already, straight from the listing.',
    do: 'It is locked, so you cannot mistype the address onto a legal document.',
  },

  /* ── branch B · straight to the form ── */
  {
    id: 'd-more',
    path: 'direct',
    chapter: 'THE PAPER', route: '/', anchor: 'tab-more', advanceOnTap: true,
    say: 'No listing this time — the buyer is ready and you write the paper now.',
    do: 'Booking forms live under More. Tap it.',
  },
  {
    id: 'd-forms',
    path: 'direct',
    chapter: 'THE PAPER', route: '/more', anchor: 'more-forms', advanceOnTap: true,
    say: 'Every form you have written is kept here.',
    do: 'Tap Booking forms.',
  },
  {
    id: 'd-fab',
    path: 'direct',
    chapter: 'THE PAPER', route: '/forms', anchor: 'forms-fab', advanceOnTap: true,
    say: 'Start a new one.',
    do: 'Tap ＋.',
  },
  {
    id: 'd-type',
    path: 'direct',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-type',
    say: 'Nobody picked the form type for you this time.',
    do: 'OTP is for a sale, OTR for a rental. We are selling, so OTP is right — leave it.',
  },
  {
    id: 'd-address',
    path: 'direct',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-address',
    say: 'Type the property address.',
    do: 'Coming from a listing this is filled in and locked. Started here, it is yours to type — so read it back before you save.',
    requireFilled: ['form-address'],
    gateHint: 'type the address to carry on',
  },
  /* ── the buyer, added for real ── */
  {
    id: 'buyer-open',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'party-purchaser-trigger', advanceOnTap: true,
    say: 'Now the buyer. Nobody has been added yet.',
    do: 'Tap the box to search your contacts.',
  },
  {
    id: 'buyer-create-new',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'party-purchaser-create-new', advanceOnTap: true,
    say: 'Your list is empty, so make a new one.',
    do: 'Tap ＋ Create new purchaser.',
  },
  {
    id: 'buyer-entity',
    // Read-only ON PURPOSE. This is a SheetSelect: tapping it opens a picker at
    // z-index 4900 whose option rows are NOT in the spotlight's hole, so the agent
    // could neither choose nor close it. Never mark a SheetSelect trigger
    // `interactive` — spotlight it, explain it, and let Next carry on.
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'quick-entity',
    say: 'First the form asks: is the buyer a person, or a company?',
    do: 'That choice decides what it wants next — an IC for a person, an SSM number for a company.',
  },
  {
    id: 'buyer-name',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'quick-name',
    // Quote the form's own label rather than assuming a person: the field reads
    // "Full name (as per IC)" or "Name (as per SSM)" depending on the choice above.
    say: () => `Fill in ${labelFor('quick-name') || 'the name'}.`,
    do: 'It goes on the agreement exactly as you type it, so spelling matters.',
    requireFilled: ['quick-name'],
    gateHint: 'type a name to carry on',
  },
  {
    id: 'buyer-ic',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'quick-reg',
    say: () => `Now ${labelFor('quick-reg') || 'the registration number'}.`,
    do: 'This and the name are the only two the form insists on — the rest can wait.',
    requireFilled: ['quick-reg'],
    gateHint: 'type the number to carry on',
  },
  {
    id: 'buyer-save',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'quick-create',
    say: 'Save them, and they drop straight onto the form.',
    do: 'Tap Create. They are kept in your contacts for next time too.',
    advanceWhen: (s) => (s.contacts || []).some((c) => c.type === 'PURCHASER'),
  },

  /* ── the owner, added the same way ── */
  {
    id: 'owner-open',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'party-owner-trigger', advanceOnTap: true,
    say: 'Now the owner — the person selling the house.',
    do: 'Tap it. You can also leave a side blank and invite them to fill themselves in — Fill & sign, on the form’s page.',
  },
  {
    id: 'owner-create-new',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'party-owner-create-new', advanceOnTap: true,
    say: 'Same again for the owner.',
    do: 'Tap ＋ Create new owner.',
  },
  /* One field per step, always. A step that demanded two spotlit only the first,
     and the coach bar then sat over the second — so the agent filled the name and
     had nowhere to go. Never ask for a field the step is not pointing at. */
  {
    id: 'owner-name',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'quick-name',
    say: () => `Fill in ${labelFor('quick-name') || 'the name'}.`,
    do: 'Same as the buyer — this is the name that goes on the agreement.',
    requireFilled: ['quick-name'],
    gateHint: 'type a name to carry on',
  },
  {
    id: 'owner-reg',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'quick-reg',
    say: () => `Now ${labelFor('quick-reg') || 'the registration number'}.`,
    do: 'The form asks for whichever number matches the type you picked.',
    requireFilled: ['quick-reg'],
    gateHint: 'type the number to carry on',
  },
  {
    id: 'owner-save',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'quick-create',
    say: 'Save the owner.',
    do: 'Tap Create.',
    advanceWhen: (s) => (s.contacts || []).some((c) => c.type === 'VENDOR'),
  },
  {
    id: 'both-parties',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-owner-1',
    say: 'Both sides are on the paper now — because we typed them in.',
    do: 'When a house already has an owner’s letter behind it, this is filled in and locked for you instead.',
  },
  /* ── the money and the terms ───────────────────────────────────────────────
     The form has twelve mandatory fields, and only three arrive with a value. The
     lesson used to tap Save straight after the parties and move on — the app
     refused the save every time, and the tour advanced anyway, teaching a save
     that had not happened. These steps are what the form actually demands. */
  {
    id: 'deal-price-listing',
    path: 'listing',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-price',
    say: 'The price came across from your listing.',
    do: 'It is a starting point, not a rule — change it here if the deal was struck at a different number.',
  },
  {
    id: 'deal-price',
    path: 'direct',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-price',
    say: 'Now the money. What did it sell for?',
    do: 'From a listing this arrives on its own. Here you type it: 680000.',
    requireFilled: ['form-price'],
    gateHint: 'type the price to carry on',
  },
  {
    /* The whole card, not one box. Six fields belong to one decision — the deposit
       ladder — and splitting them would be six near-identical steps. Anchoring the
       card keeps every one of them inside the spotlight and the coach bar outside
       it, which is the condition a multi-field step has to meet. */
    id: 'deal-deposits',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-money-card',
    say: 'These are the deposit terms, and the form will not save without them.',
    do: 'The usual ones: 3% earnest, 7% balance deposit, 90% on completion, 3 months to complete, 1 month grace, 8% interest if they are late.',
    requireFilled: ['form-earnest', 'form-bal-dep', 'form-bal-pur', 'form-bal-mth', 'form-grace', 'form-interest'],
    gateHint: 'fill every box in this card to carry on',
  },
  {
    id: 'deal-stake-open',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-stakeholder', advanceOnTap: true,
    say: 'Who holds the deposit until the sale goes through?',
    do: 'Tap the box.',
  },
  {
    id: 'deal-stake-pick',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'stakeholder-opt-cid', advanceOnTap: true,
    say: 'On this deal the money sits with the company.',
    do: 'Tap CID REALTORS SDN BHD. A solicitor can hold it instead — that is what the other two are for.',
  },
  {
    id: 'deal-other',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-other-terms',
    say: 'Last box — anything else the two sides agreed.',
    do: 'Write “Nil” if there was nothing. The form insists on an answer either way.',
    requireFilled: ['form-other-terms'],
    gateHint: 'write something — “Nil” is a fine answer',
  },
  {
    id: 'form-save',
    chapter: 'THE PAPER', ...AT_FORM, anchor: 'form-save',
    say: 'Save it, and you have an offer form.',
    do: 'Tap Save draft. Nothing is in force yet — a draft can be saved half-finished and completed later.',
    // Waits for the form to actually EXIST, not for the tap. Miss a mandatory
    // field and the app refuses the save; the tour has to refuse it too, or the
    // lesson quietly teaches that an incomplete form goes through.
    advanceWhen: (s) => s.formCreated,
  },

  /* ── signing, for real ── */
  {
    id: 'signing-tab',
    chapter: 'THE PAPER', route: `/forms/${L.otp}`, block: 'form-sign', anchor: 'form-tab-signing', advanceOnTap: true,
    say: 'Now the buyer and the owner sign it.',
    do: 'Tap the Signing tab.',
  },
  {
    id: 'signing-progress',
    chapter: 'THE PAPER', ...AT_OTP_SIGNING, anchor: 'form-signing-progress',
    say: 'This counts who has signed.',
    do: 'Watch it move as each person signs.',
  },
  {
    id: 'sign-open',
    chapter: 'THE PAPER', ...AT_OTP_SIGNING, anchor: 'form-sign-on-device', advanceOnTap: true,
    say: 'The buyer is here with you, so they sign on your phone.',
    do: 'Tap Sign on device.',
  },
  {
    id: 'sign-draw',
    // The whole pad, not just the button: the agent has to reach the canvas, and
    // the button stays disabled until they have drawn on it.
    chapter: 'THE PAPER', ...AT_OTP_SIGNING, anchor: 'form-sign-pad',
    say: 'Hand them the phone and let them draw their name.',
    do: 'Draw anything in the box, then tap Capture & submit.',
    advanceWhen: (s) => s.otpSigned.length >= 1,
  },
  {
    // Split for the same reason the buyer's signature is: one step cannot ask the
    // agent to tap a button AND then draw inside the dialog it opens. The moment
    // the pad opens, a spotlight still aimed at the button sits behind the modal,
    // and the canvas the agent actually needs is under a blocker.
    id: 'sign-second-open',
    chapter: 'THE PAPER', ...AT_OTP_SIGNING, anchor: 'form-sign-on-device', advanceOnTap: true,
    say: 'One down. Now the owner signs too.',
    do: 'Tap Sign on device on the owner’s row.',
  },
  {
    id: 'sign-second-draw',
    chapter: 'THE PAPER', ...AT_OTP_SIGNING, anchor: 'form-sign-pad',
    say: 'Same again — draw the owner’s name.',
    do: 'Then tap Capture & submit.',
    advanceWhen: (s) => s.otpStatus === 'SIGNED',
  },
  {
    id: 'all-signed',
    chapter: 'THE PAPER', ...AT_OTP_SIGNING, anchor: 'form-ready-to-issue',
    say: 'Everyone has signed, so the form turned green.',
    do: 'Signed is not the same as in force. One more step.',
  },
  {
    id: 'issue-open',
    chapter: 'THE PAPER', ...AT_OTP_SIGNING, anchor: 'form-issue-btn', advanceOnTap: true,
    say: 'Now switch it on.',
    do: 'Tap Issue form. No manager approves this — it is your call.',
  },
  {
    id: 'issue-confirm',
    chapter: 'THE PAPER', ...AT_OTP_SIGNING, anchor: 'form-issue-confirm',
    say: 'Read what issuing does, then confirm.',
    do: 'Tap Issue form again in the box.',
    advanceWhen: (s) => s.otpStatus === 'APPROVED',
  },
  {
    id: 'under-offer',
    path: 'listing',
    chapter: 'THE PAPER', ...AT_OTP, block: 'form-sign', anchor: 'form-doc-bar',
    say: 'Issuing also booked the house for this buyer, all by itself.',
    do: 'The listing is Under Offer now. Your document lives behind this bar — you can open or print it any time.',
  },
  {
    // Same moment, different truth: a form written from scratch is linked to no
    // listing, so issuing it books nothing. Saying "the listing is Under Offer"
    // here would point at a listing that does not exist.
    id: 'under-offer-direct',
    path: 'direct',
    chapter: 'THE PAPER', ...AT_OTP, block: 'form-sign', anchor: 'form-doc-bar',
    say: 'The offer is in force.',
    do: 'There was no listing behind this one, so nothing was booked — that is the one thing you give up by starting here. Your document lives behind this bar.',
  },

  /* ── the sale, and the stage board ── */
  {
    id: 'sale-open',
    chapter: 'THE DEAL', route: `/sales/${L.sale}`, anchor: 'sale-stage-card',
    say: 'The offer became a deal. This is where it lives from now on.',
    do: 'The stage is how you tell head office where the deal has got to.',
  },
  {
    id: 'stage-open',
    chapter: 'THE DEAL', ...AT_STAGE, anchor: 'sale-stage-change', advanceOnTap: true,
    say: 'You move the stage yourself as the deal moves.',
    do: 'Tap Change to see your choices.',
  },
  {
    id: 'stage-balance',
    chapter: 'THE DEAL', ...AT_STAGE, readOnly: true, anchor: 'stage-opt-BALANCE_TO',
    say: 'Use this one when you have worked out the balance for the owner.',
    do: 'Not now though — read it and carry on.',
  },
  {
    id: 'stage-pick',
    chapter: 'THE DEAL', ...AT_STAGE, anchor: 'stage-opt-REQUEST_INVOICE',
    say: 'Today you want head office to bill the buyer.',
    do: 'Tap Request invoice, then confirm.',
    advanceWhen: (s) => s.saleStage === 'REQUEST_INVOICE',
  },
  {
    id: 'stage-done',
    chapter: 'THE DEAL', route: `/sales/${L.sale}`, anchor: 'sale-stage-card',
    say: 'The stage moved, and head office can see it.',
    do: 'It is only a message. It creates nothing and changes no money.',
  },

  /* ── the money ── */
  {
    id: 'hq-invoice',
    chapter: 'THE MONEY', route: `/sales/${L.sale}`, anchor: 'sale-stage-card',
    actor: 'HQ', beat: 'invoice',
    // What head office actually does when the first invoice is raised — the
    // commission engine runs here, which is why the figures lock.
    hqSteps: ['Checking your paperwork', 'Working out the commission', 'Raising the invoice', 'Locking the figures'],
    say: 'Head office has prepared the invoice.',
    do: 'They raise it — sending it to the buyer is your job. Your commission is worked out now and the numbers are locked.',
    toast: { title: 'Invoice issued on SS-DEMO-01', body: 'Your commission figures are locked from here.', tone: 'warn' },
  },
  /* ── the bill: find it, send it, then bank the payment ── */
  {
    id: 'inv-tab',
    chapter: 'THE MONEY', ...AT_SALE, anchor: 'sale-tab-invoices', advanceOnTap: true,
    say: 'The invoice is waiting for you on the Invoices tab.',
    do: 'We have scrolled the tabs across for you — tap Invoices.',
  },
  {
    id: 'inv-row',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=invoices`, readOnly: true, anchor: 'invoice-row',
    say: 'There it is — what the buyer owes, and what is still outstanding.',
    do: 'Head office wrote it, but nobody has sent it anywhere yet. That part is yours.',
  },
  {
    id: 'inv-open',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=invoices`, anchor: 'invoice-view-pdf',
    say: 'Open it to read the actual document.',
    do: 'Tap View PDF.',
    advanceWhenDom: () => !!document.querySelector('[data-tour="file-share"]'),
  },
  {
    id: 'inv-share',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=invoices`, block: 'invoice-view', anchor: 'file-share', advanceOnTap: true,
    say: 'Now YOU send it to the buyer — head office never does.',
    do: 'Share puts it straight into WhatsApp or email. You can print it from here too.',
  },
  {
    id: 'inv-close',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=invoices`, block: 'invoice-view', anchor: 'file-close', advanceOnTap: true,
    say: 'Sent. Now you chase the payment.',
    do: 'Close the document — head office will not follow this up for you.',
  },

  {
    id: 'rcpt-tab',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=invoices`, anchor: 'sale-tab-receipts', advanceOnTap: true,
    say: 'The buyer has paid you. Now tell head office.',
    do: 'Tap Receipts.',
  },
  {
    id: 'rcpt-add',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=receipts`, anchor: 'receipt-add', advanceOnTap: true,
    say: 'Record what you received.',
    do: 'Tap ＋ Record a payment receipt.',
  },
  {
    id: 'rcpt-amount',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=receipts`, block: 'receipt-sheet', anchor: 'receipt-amount',
    say: 'How much did they pay?',
    do: 'The earnest deposit on this deal is RM 20,400.',
    requireFilled: ['receipt-amount'],
    gateHint: 'type the amount to carry on',
  },
  {
    id: 'rcpt-slip',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=receipts`, block: 'receipt-sheet', readOnly: true, anchor: 'receipt-link-slip',
    say: 'Attach the bank-in slip so finance can check it.',
    do: 'On a real deal you would photograph it. We have put one on this practice sale already.',
  },
  {
    id: 'rcpt-submit',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=receipts`, block: 'receipt-sheet', anchor: 'receipt-submit',
    say: 'Send it to head office.',
    do: 'Tap Record receipt.',
    advanceWhen: (s) => (s.receipts || []).length > 0,
  },
  {
    id: 'rcpt-pending',
    chapter: 'THE MONEY', route: `/sales/${L.sale}?tab=receipts`, readOnly: true, anchor: 'receipt-row',
    say: 'It says Pending — nothing has moved yet.',
    do: 'Finance has to check it against the bank before any money is yours.',
  },

  {
    id: 'hq-release',
    chapter: 'THE MONEY', route: '/earnings', anchor: 'earnings-period-tiles',
    actor: 'HQ', beat: 'release',
    hqSteps: ['Matching your slip to the bank', 'Approving the receipt', 'Applying the cash to the invoice', 'Freeing your share'],
    say: 'Finance checked your slip, and your money is unlocked.',
    do: 'Nothing tells you this one — the chip just changes. Come and look.',
    toast: { title: 'Receipt approved — RM 20,400', body: 'Finance has validated it and credited the sale.', tone: 'ok' },
  },
  {
    id: 'hq-payout',
    chapter: 'THE MONEY', route: '/earnings', anchor: 'earnings-period-tiles',
    actor: 'HQ', beat: 'payout',
    hqSteps: ['Preparing the pay run', 'Paying your share out', 'Attaching the payment voucher'],
    say: 'The money is yours. You are done.',
    do: 'Sold RM 680,000 → fee RM 13,600 → your 70% tier = RM 9,520.',
    toast: { title: 'Payout paid — RM 9,520', body: 'Residensi Amanina, A-12-03 · SS-DEMO-01', tone: 'ok' },
  },
];

/* ═══ LESSON 4 · sharing a deal with another REN ══════════════════════════
   The agent is the LISTER throughout: it is their house, another REN brings the
   buyer, and every screen here is one the lister drives. The peer's two moves —
   asking, and signing their half of the agreement — are the only simulated beats,
   because they genuinely happen on someone else's phone.

   The payoff is the arithmetic. Same house, same RM 13,600 fee, but shared: the
   agent watches RM 9,520 become RM 4,760 and can see exactly where it halved. */
const AT_CSR = { route: `/co-share/${L.csr}` };
// Accepting settles the split, and the sheet is local state — a fresh mount has it
// shut, so these steps only work from the one that opens it.
const AT_CS_ACCEPT = { route: `/co-share/${L.csr}`, block: 'cs-accept' };
// The sale wizard is three screens of one component's state. Backing into the
// middle of it would show a step over a form that has been rebuilt empty.
const AT_CS_SALE = { route: '/sales/new', navState: () => CS_SALE_STATE, block: 'cs-sale' };
const AT_CSF = { route: `/co-share-forms/${L.csform}` };

/**
 * What CoShareDetailPage hands to the sale form. Reproduced here so /sales/new is
 * reachable by Back and by a resume, not only by tapping forward into it.
 */
const CS_SALE_STATE = {
  fromCoShare: {
    id: L.csr,
    listing_id: L.listing,
    listing_ref: 'PL-DEMO-01',
    listing_type: 'SUBSALE',
    property_address: 'A-12-03, Residensi Amanina, Jalan Amanina 3',
    asking_price_rm: 680000,
    agreed_split_percentage: 50,
    requester: { id: 'demo-agent-peer', name: 'Farah binti Idris (demo)' },
    lister: null,
  },
};

const COSHARE = [
  /* ── the ask ── */
  {
    id: 'cs-intro',
    chapter: 'THE ASK', route: '/',
    say: 'Someone else has a buyer for your house. This is how you share the deal.',
    do: 'Not a co-agency — that is a different company. This is another CID agent, and you split the same fee.',
  },
  {
    id: 'cs-peer-asks',
    chapter: 'THE ASK', route: '/',
    actor: 'PEER', beat: 'peer-asks',
    hqSteps: ['Reading your listing', 'Writing you a message', 'Proposing a 50/50 split', 'Sending the request'],
    say: 'Farah has a buyer and wants in on your listing.',
    do: 'She can only ask because your listing is Public. Internal listings nobody else can see.',
    toast: { title: 'Farah binti Idris asked to co-share PL-DEMO-01', body: 'She proposes a 50/50 split. Accept or decline.', tone: 'warn' },
  },
  {
    id: 'cs-more',
    chapter: 'THE ASK', route: '/', anchor: 'tab-more', advanceOnTap: true,
    say: 'Her request is waiting for you.',
    do: 'Tap More.',
  },
  {
    id: 'cs-menu',
    chapter: 'THE ASK', route: '/more', anchor: 'more-co-share', advanceOnTap: true,
    say: 'Co-share is where both sides of these live.',
    do: 'Tap it.',
  },
  {
    id: 'cs-boxes',
    chapter: 'THE ASK', route: '/co-share', anchor: 'cs-box-incoming',
    say: 'Two boxes. Incoming is what people ask of YOU.',
    do: 'Outgoing is what you asked of them — you would use that to get onto someone else’s listing.',
  },
  {
    id: 'cs-open',
    chapter: 'THE ASK', route: '/co-share', anchor: 'cs-request-row', advanceOnTap: true,
    say: 'Here is Farah’s ask.',
    do: 'Tap it to read what she wants.',
  },
  {
    id: 'cs-read',
    chapter: 'THE ASK', ...AT_CSR, anchor: 'cs-terms',
    say: 'She proposes 50% of the fee, and says why.',
    do: 'Nothing has happened yet. Reading it costs you nothing — accepting is the decision.',
  },

  /* ── the answer ── */
  {
    id: 'cs-accept-open',
    chapter: 'THE ANSWER', ...AT_CS_ACCEPT, anchor: 'cs-accept', advanceOnTap: true,
    say: 'You want the deal. Say yes.',
    do: 'Tap Accept. Decline is beside it, and it is final — she would have to ask again.',
  },
  {
    id: 'cs-split',
    chapter: 'THE ANSWER', ...AT_CS_ACCEPT, anchor: 'cs-split',
    say: 'This is the number that matters. It is HER share, not yours.',
    do: 'She asked for 50. Leave it, or type what you actually agreed — whatever is here becomes the split on the sale.',
    requireFilled: ['cs-split'],
    gateHint: 'put a number in to carry on',
  },
  {
    id: 'cs-accept-send',
    chapter: 'THE ANSWER', ...AT_CS_ACCEPT, anchor: 'cs-accept-submit',
    say: 'Send it back to her.',
    do: 'Tap Accept co-share. This still creates nothing — it is a handshake, not a deal.',
    advanceWhen: (s) => s.csStatus !== 'PENDING',
  },

  /* ── the deal ── */
  {
    id: 'cs-create-sale',
    chapter: 'THE DEAL', ...AT_CSR, anchor: 'cs-create-sale', advanceOnTap: true,
    say: 'Weeks pass, the buyer signs, and the deal is done. Now you raise the sale.',
    do: 'YOU raise it, not Farah — it is your listing. Tap Create sale from this co-share.',
  },
  {
    id: 'cs-sale-banner',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'sale-cs-banner',
    say: 'The sale form knows about the co-share already.',
    do: 'The property, the price and both agents came across from the request.',
  },
  {
    id: 'cs-sale-next1',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'sale-next', advanceOnTap: true,
    say: 'The deal details are set by the listing, so there is nothing to change here.',
    do: 'Tap Next · Parties.',
  },
  {
    id: 'cs-vendor-open',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'sale-vendor', advanceOnTap: true,
    say: 'Who is selling? Your contacts are already here from earlier deals.',
    do: 'Tap the box.',
  },
  {
    id: 'cs-vendor-pick',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'contact-opt-vendor', advanceOnTap: true,
    say: 'The owner of the house.',
    do: 'Tap Lim Wei Sheng.',
  },
  {
    id: 'cs-purchaser-open',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'sale-purchaser', advanceOnTap: true,
    say: 'And the buyer — the one Farah brought.',
    do: 'Tap the box.',
  },
  {
    id: 'cs-purchaser-pick',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'contact-opt-purchaser', advanceOnTap: true,
    say: 'Her buyer goes on your sale.',
    do: 'Tap Nurul Aisyah.',
  },
  {
    id: 'cs-split-card',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'sale-split',
    say: 'Both RENs are on the deal, at the split you agreed.',
    do: 'Farah cannot be removed — the sale was raised from her co-share. The percentages are still yours to change, and they must total 100.',
  },
  {
    id: 'cs-sale-next2',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'sale-next', advanceOnTap: true,
    say: 'On to the money.',
    do: 'Tap Next · Price.',
  },
  {
    // The co-share carries the PRICE across but not the rate — the fee is
    // negotiated per deal, so the form asks and refuses to save without it.
    id: 'cs-sale-rate',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'sale-comm-pct',
    say: 'The price came across from the listing. Your fee did not — that is per deal.',
    do: 'Type 2. The total fills itself in: 2% of RM 680,000 is RM 13,600.',
    requireFilled: ['sale-comm-pct'],
    gateHint: 'type the rate to carry on',
  },
  {
    id: 'cs-sale-whole-fee',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'sale-comm-pct',
    say: 'Note what that figure is: the WHOLE fee, RM 13,600.',
    do: 'The split has not been taken off yet. It comes off when head office works out what each REN is owed.',
  },
  {
    id: 'cs-sale-save',
    chapter: 'THE DEAL', ...AT_CS_SALE, anchor: 'sale-next',
    say: 'Raise it.',
    do: 'Tap Create sale. Farah is added to it at the agreed split.',
    advanceWhen: (s) => s.csSaleCreated,
  },

  /* ── the paper ── */
  {
    id: 'cs-atts',
    chapter: 'THE PAPER', route: `/sales/${L.sale}`, anchor: 'sale-tab-attachments', advanceOnTap: true,
    say: 'Two RENs on one fee needs an agreement you both sign.',
    do: 'Tap Attachments — it lives with the sale’s documents.',
  },
  {
    id: 'cs-generate',
    chapter: 'THE PAPER', route: `/sales/${L.sale}?tab=attachments`, anchor: 'csf-generate',
    say: 'You do not fill this one in. It is built from the sale.',
    do: 'Tap Generate.',
    advanceWhen: (s) => !!s.csForm,
  },
  {
    id: 'cs-form-split',
    chapter: 'THE PAPER', ...AT_CSF, anchor: 'csf-split',
    say: 'Here is the fee, and who gets what.',
    do: 'Every figure came off the sale. If it is wrong, the sale is wrong — fix it there, not here.',
  },
  {
    id: 'cs-form-read',
    chapter: 'THE PAPER', ...AT_CSF, block: 'cs-doc', anchor: 'csf-read', advanceOnTap: true,
    say: 'Read what you are about to put your name to.',
    do: 'Tap Read the document.',
  },
  {
    // The viewer covers the whole screen, Issue included. Without this step the
    // next one points at a button underneath an open document.
    id: 'cs-form-close',
    // DocViewer, not FileViewer — the co-share form is live HTML, the invoice was a
    // PDF, and they are different components with different controls.
    chapter: 'THE PAPER', ...AT_CSF, block: 'cs-doc', anchor: 'doc-close', advanceOnTap: true,
    say: 'That is the agreement. Clause 5 is the one worth knowing.',
    do: 'Close it — you sign back on the form.',
  },
  {
    id: 'cs-form-issue',
    chapter: 'THE PAPER', ...AT_CSF, anchor: 'csf-issue',
    say: 'Issuing fixes the figures and asks Farah to sign.',
    do: 'Tap Issue & request signatures, then confirm.',
    advanceWhen: (s) => s.csForm === 'PENDING_SIGNING',
  },
  {
    id: 'cs-form-sign-open',
    chapter: 'THE PAPER', ...AT_CSF, block: 'cs-form-sign', anchor: 'csf-sign', advanceOnTap: true,
    say: 'You sign your own row. Nobody can sign for you.',
    do: 'Tap Sign this form.',
  },
  {
    id: 'cs-form-sign-draw',
    chapter: 'THE PAPER', route: `/co-share-forms/${L.csform}`, block: 'cs-form-sign', anchor: 'csf-sign-pad',
    say: 'Draw your name.',
    do: 'Then tap Sign form.',
    advanceWhen: (s) => s.csFormSigned.length >= 1,
  },
  {
    id: 'cs-peer-signs',
    chapter: 'THE PAPER', ...AT_CSF,
    actor: 'PEER', beat: 'peer-signs',
    hqSteps: ['Opening the agreement', 'Reading the split', 'Signing her row'],
    say: 'Farah signs hers, on her own phone.',
    do: 'You cannot do this bit for her — which is the point of a document you both sign.',
    toast: { title: 'Farah binti Idris signed ICS-DEMO-01', body: 'Every REN has now signed the co-share agreement.', tone: 'ok' },
  },
  {
    id: 'cs-form-done',
    chapter: 'THE PAPER', ...AT_CSF, anchor: 'csf-split',
    say: 'Both signed. The split is now on paper.',
    do: 'If the split ever changes you reissue it — you never edit a signed agreement.',
  },

  /* ── the money ── */
  {
    id: 'cs-stage-open',
    chapter: 'THE MONEY', ...AT_STAGE, anchor: 'sale-stage-change', advanceOnTap: true,
    say: 'From here it is an ordinary deal.',
    do: 'Tap Change.',
  },
  {
    id: 'cs-stage-pick',
    chapter: 'THE MONEY', ...AT_STAGE, anchor: 'stage-opt-REQUEST_INVOICE',
    say: 'Ask head office to bill the buyer.',
    do: 'Tap Request invoice, then confirm.',
    advanceWhen: (s) => s.saleStage === 'REQUEST_INVOICE',
  },
  {
    id: 'cs-hq-invoice',
    chapter: 'THE MONEY', route: `/sales/${L.sale}`, anchor: 'sale-stage-card',
    actor: 'HQ', beat: 'invoice',
    hqSteps: ['Checking your paperwork', 'Reading the co-share split', 'Working out both shares', 'Raising the invoice'],
    say: 'Head office bills the buyer and works out both shares.',
    do: 'The co-share form is what tells them the fee is split — and it is filed on this sale.',
    toast: { title: 'Invoice issued on SS-DEMO-01', body: 'Your commission figures are locked from here.', tone: 'warn' },
  },
  {
    id: 'cs-earn-locked',
    chapter: 'THE MONEY', route: '/earnings', anchor: 'earnings-period-tiles',
    say: 'And here is what sharing costs you.',
    do: 'The house sold for the same RM 680,000, and the fee is the same RM 13,600 — but half of it is Farah’s.',
  },
  {
    id: 'cs-hq-release',
    chapter: 'THE MONEY', route: '/earnings', anchor: 'earnings-period-tiles',
    actor: 'HQ', beat: 'release',
    hqSteps: ['Banking the buyer’s payment', 'Approving the receipt', 'Freeing both RENs’ shares'],
    say: 'The money comes in and your share is unlocked.',
    do: 'Farah’s half is released to her at the same time, on her own earnings page.',
    toast: { title: 'Receipt approved — RM 20,400', body: 'Finance has validated it and credited the sale.', tone: 'ok' },
  },
  {
    id: 'cs-hq-payout',
    chapter: 'THE MONEY', route: '/earnings', anchor: 'earnings-period-tiles',
    actor: 'HQ', beat: 'payout',
    hqSteps: ['Preparing the pay run', 'Paying your share out', 'Attaching the payment voucher'],
    say: 'Paid. That is a shared deal, end to end.',
    do: 'RM 13,600 fee → half yours = RM 6,800 → your 70% tier = RM 4,760. Alone it would have been RM 9,520 — but alone you had no buyer.',
    toast: { title: 'Payout paid — RM 4,760', body: 'Residensi Amanina, A-12-03 · SS-DEMO-01 · co-shared', tone: 'ok' },
  },
];

/* ═══ LESSON 5 · the owner's letter, and what it feeds ════════════════════
   Three documents, in the order a real deal produces them:

     ALS   the owner signs: you may sell my house, at this price, for this fee
       ↓   Convert to listing
     LISTING  what buyers see — address, owner and price come from the letter
       ↓   Generate booking form
     OTP   the buyer signs — and the owner's details arrive LOCKED, because they
           were agreed and signed before any buyer existed

   The lesson opens on an ALS that is already issued. Writing one is fifteen
   mandatory fields and five pickers; that is a different lesson, and putting it
   here would bury the linkage under twenty steps of typing. What this teaches is
   what the letter DOES once it exists — which is the part agents get wrong. */
const AT_ALS = { route: `/forms/${L.als}` };
/**
 * The OTP steps of THIS lesson, which must carry the letter as well as the
 * listing. The spine's AT_FORM hardcodes `fromAuthForm: null` — correct there,
 * since that listing has no letter — so reusing it here would quietly strip the
 * very link the lesson is about the moment the agent pressed Back.
 */
const AT_FORM_ALS = {
  route: '/forms/new',
  navState: () => ({ ...FROM_LISTING_ALS }),
  block: 'chain-form',
};
const AT_LISTING_NEW = { route: '/listings/new', navState: () => FROM_ALS, block: 'chain-listing' };

/** What FormDetailPage hands to the listing form. Reproduced so /listings/new is
 *  reachable by Back and by a resume, not only by tapping forward into it. */
const FROM_ALS = {
  fromAuthForm: {
    id: L.als,
    form_ref: 'ALS-DEMO-01',
    sale_type: 'SALES',
    branch_id: null,
    form_data: {
      property_address: 'A-12-03, Residensi Amanina, Jalan Amanina 3',
      owner_1_id: 'demo-contact-v',
      owner_1_name: 'Lim Wei Sheng (demo)',
      owner_1_nric: '640909-10-5099',
      selling_price_rm: 680000,
      earnest_deposit_percent: 3,
      balance_deposit_percent: 7,
      balance_purchase_price_percent: 90,
      balance_month: 3,
      grace_period: 1,
      interest_percent: 8,
      professional_fee_type: '2% exclusive of 8% SST',
      vacant_possession: true,
    },
  },
};

/** What ListingDetailPage hands to /forms/new for a letter-backed listing. */
const FROM_LISTING_ALS = {
  fromListing: {
    id: L.listing,
    listing_ref: 'PL-DEMO-01',
    listing_type: 'SUBSALE',
    property_title: 'Residensi Amanina A-12-03 (demo)',
    property_address: 'A-12-03, Residensi Amanina, Jalan Amanina 3',
    asking_price_rm: 680000,
    area: 'Petaling Jaya',
    state: 'Selangor',
    property_type: 'Condominium',
    is_mine: true,
  },
  fromAuthForm: {
    id: L.als,
    form_ref: 'ALS-DEMO-01',
    sale_type: 'SALES',
    form_data: FROM_ALS.fromAuthForm.form_data,
  },
};

const CHAIN = [
  /* ── the letter ── */
  {
    id: 'ch-intro',
    chapter: 'THE LETTER', route: '/',
    say: 'Three papers, and each one feeds the next.',
    do: 'The owner’s letter, then the listing, then the buyer’s offer. Get the first one right and the other two fill themselves in.',
  },
  {
    id: 'ch-more',
    chapter: 'THE LETTER', route: '/', anchor: 'tab-more', advanceOnTap: true,
    say: 'You already wrote the letter and the owner signed it last week.',
    do: 'Tap More to find it.',
  },
  {
    id: 'ch-menu',
    chapter: 'THE LETTER', route: '/more', anchor: 'more-forms', advanceOnTap: true,
    say: 'Letters and offers live in the same place.',
    do: 'Tap Booking forms.',
  },
  {
    id: 'ch-open',
    chapter: 'THE LETTER', route: '/forms', anchor: 'forms-row',
    say: 'ALS — Authority to Sell. That is the owner appointing you.',
    do: 'ALR is the rental version. OTP and OTR are what a BUYER signs — different paper, different signer.',
  },
  {
    id: 'ch-open-tap',
    chapter: 'THE LETTER', route: '/forms', anchor: 'forms-row', advanceOnTap: true,
    say: 'Open it.',
    do: 'Tap the ALS.',
  },
  {
    id: 'ch-terms',
    chapter: 'THE LETTER', ...AT_ALS, anchor: 'form-key-terms',
    say: 'This is what the owner agreed to, in writing.',
    do: 'The price, your fee, and how long you have. Nobody can quietly change these later — that is the whole point of getting it signed.',
  },

  /* ── the listing ── */
  {
    id: 'ch-convert',
    chapter: 'THE LISTING', ...AT_ALS, anchor: 'form-convert-listing', advanceOnTap: true,
    say: 'Now advertise it. You do not start a listing from scratch.',
    do: 'Tap Convert to listing.',
  },
  {
    id: 'ch-linked',
    chapter: 'THE LISTING', ...AT_LISTING_NEW, anchor: 'listing-auth-link',
    say: 'The listing arrives already tied to the letter.',
    do: 'You can also link one here on a listing you started by hand — same result, same lock.',
  },
  {
    id: 'ch-locked',
    chapter: 'THE LISTING', ...AT_LISTING_NEW, anchor: 'listing-locked-banner',
    say: 'And these three are now fixed: address, owner, price.',
    do: 'Greyed out, not missing. To change them you clear the link — because they would no longer match what the owner signed.',
  },
  {
    id: 'ch-address-locked',
    chapter: 'THE LISTING', ...AT_LISTING_NEW, anchor: 'listing-address',
    say: 'Try it if you like — it will not take a keystroke.',
    do: 'This is the address off the letter, and the listing cannot disagree with the letter.',
  },
  {
    id: 'ch-title',
    chapter: 'THE LISTING', ...AT_LISTING_NEW, anchor: 'listing-title',
    say: 'What is yours is the advertising.',
    do: 'Give it a title buyers will read — the letter says nothing about that.',
    requireFilled: ['listing-title'],
    gateHint: 'type a title to carry on',
  },
  {
    // The letter says nothing about where the property IS for search purposes, and
    // the listing will not publish without it — one field, because the postcode
    // fills the city and the state itself.
    id: 'ch-postcode',
    chapter: 'THE LISTING', ...AT_LISTING_NEW, anchor: 'listing-postcode',
    say: 'One thing the letter cannot give you: the postcode.',
    do: 'Type 46000. The city and state fill themselves in, and buyers search on them.',
    requireFilled: ['listing-postcode'],
    gateHint: 'type the postcode to carry on',
  },
  {
    id: 'ch-publish',
    chapter: 'THE LISTING', ...AT_LISTING_NEW, anchor: 'listing-publish',
    say: 'Put it up.',
    do: 'Publish makes it visible. Save draft keeps it to yourself — a draft is private and never public.',
    // Waits for the PUBLISH, not the create. Publishing is two calls — the
    // listing is written as a draft and then switched live — and gating on the
    // first let a failed second half slide past unnoticed.
    advanceWhen: (s) => s.listingPublished,
  },
  {
    id: 'ch-listing-row',
    chapter: 'THE LISTING', route: `/listings/${L.listing}`, anchor: 'listing-auth-row',
    say: 'The listing now names its letter.',
    do: 'Anyone looking at this listing can see which signed authority stands behind it.',
  },

  /* ── the offer ── */
  {
    id: 'ch-generate',
    chapter: 'THE OFFER', route: `/listings/${L.listing}`, anchor: 'listing-primary-action', advanceOnTap: true,
    say: 'A buyer turns up. Write the offer from the listing, not from scratch.',
    do: 'Tap Generate booking form.',
  },
  {
    id: 'ch-banner',
    chapter: 'THE OFFER', ...AT_FORM_ALS, anchor: 'form-from-listing-banner',
    say: 'Read this line — it names the letter, not just the listing.',
    do: 'The property and the owner come from ALS-DEMO-01 and are fixed. The price and terms are pre-filled from it too.',
  },
  {
    id: 'ch-owner-locked',
    chapter: 'THE OFFER', ...AT_FORM_ALS, anchor: 'form-owner-1',
    say: 'The owner is already on the offer, and locked.',
    do: 'In the other lesson you typed this by hand. Here the letter did it — same person, verified, with no chance of a typo on a legal document.',
  },
  {
    id: 'ch-price-locked',
    chapter: 'THE OFFER', ...AT_FORM_ALS, anchor: 'form-price',
    say: 'The price and the deposit ladder came across too.',
    do: 'These are pre-filled, NOT locked — change them if the deal was actually struck at a different number.',
  },
  {
    id: 'ch-buyer-left',
    chapter: 'THE OFFER', ...AT_FORM_ALS, anchor: 'form-purchaser-1',
    say: 'One name is still missing, and only one.',
    do: 'The buyer — nobody could have known them when the owner signed. Everything else was decided weeks ago.',
  },
  {
    id: 'ch-done',
    chapter: 'THE OFFER', ...AT_FORM_ALS, anchor: 'form-from-listing-banner',
    say: 'That is the chain.',
    do: 'Letter → listing → offer. Skip the letter and you type all of it yourself, three times, with three chances to get it wrong.',
  },
];

/* ═══ LESSON 2 · the stage board on its own ═══════════════════════════════ */
const STAGES = [
  {
    id: 'st-card',
    chapter: 'STAGES', route: `/sales/${L.sale}`, anchor: 'sale-stage-card',
    say: 'The stage tells head office where a deal has got to.',
    do: 'It never moves by itself. You set it.',
  },
  {
    id: 'st-open',
    chapter: 'STAGES', ...AT_STAGE, anchor: 'sale-stage-change', advanceOnTap: true,
    say: 'These are the six you can set.',
    do: 'Tap Change.',
  },
  {
    id: 'st-draft',
    chapter: 'STAGES', ...AT_STAGE, readOnly: true, anchor: 'stage-opt-DRAFT',
    say: 'Draft — you are still putting the deal together.',
    do: 'Every new deal starts here.',
  },
  {
    id: 'st-balance',
    chapter: 'STAGES', ...AT_STAGE, readOnly: true, anchor: 'stage-opt-BALANCE_TO',
    say: 'Balance to owner — you have worked out what the owner gets.',
    do: 'Use it once the sums are passed on.',
  },
  {
    id: 'st-invoice',
    chapter: 'STAGES', ...AT_STAGE, readOnly: true, anchor: 'stage-opt-REQUEST_INVOICE',
    say: 'Request invoice — please bill the buyer.',
    do: 'Use it when the deal is firm and you want the money moving.',
  },
  {
    // The counterpart to st-invoice: that one asks for the bill going OUT, this
    // one asks for the receipt going BACK once the money has come in. The pair
    // reads as a pair in the sheet, so it is taught as one here too.
    id: 'st-receipt',
    chapter: 'STAGES', ...AT_STAGE, readOnly: true, anchor: 'stage-opt-REQUEST_RECEIPT',
    say: 'Request receipt — the customer has paid, please issue the official receipt.',
    do: 'Record the payment first, then ask. Head office issues the receipt, not you.',
  },
  {
    id: 'st-claim',
    chapter: 'STAGES', ...AT_STAGE, readOnly: true, anchor: 'stage-opt-READY_TO_CLAIM',
    say: 'Ready to claim — all my paperwork is in.',
    do: 'This is the last one you can set. After it, head office takes over.',
  },
  {
    id: 'st-cancel',
    chapter: 'STAGES', ...AT_STAGE, readOnly: true, anchor: 'stage-opt-CANCEL_REFUND_LOAN_REJECT',
    say: 'Cancel — the deal fell through.',
    do: 'Loan rejected, or the buyer walked away. Tell head office early.',
  },
  {
    id: 'st-try',
    chapter: 'STAGES', ...AT_STAGE, anchor: 'stage-opt-READY_TO_CLAIM',
    say: 'Your turn — set this deal to Ready to claim.',
    do: 'Tap it, then confirm.',
    advanceWhen: (s) => s.saleStage === 'READY_TO_CLAIM',
  },
  {
    id: 'st-done',
    chapter: 'STAGES', route: `/sales/${L.sale}`, anchor: 'sale-stage-card',
    say: 'That is the whole stage board.',
    do: 'They are not in order — pick whichever is true today.',
  },
];

/* ═══ LESSON 3 · finding your way around ══════════════════════════════════ */
const AROUND = [
  {
    id: 'more',
    chapter: 'FINDING THINGS', route: '/more',
    say: 'Everything that is not a tab lives in More.',
    do: 'Booking forms, letters, co-share, contacts and your profile.',
  },
  {
    id: 'more-forms',
    chapter: 'FINDING THINGS', route: '/more', anchor: 'more-forms',
    say: 'Booking forms are the papers you write.',
    do: 'OTP and OTR are what the buyer signs. ALS and ALR are what the owner signs.',
  },
  {
    id: 'more-letters',
    chapter: 'FINDING THINGS', route: '/more', anchor: 'more-letters',
    say: 'Co-agency letters are for an agency outside CID.',
    do: 'A different company. Their cut comes off the top, before your share.',
  },
  {
    id: 'more-coshare',
    chapter: 'FINDING THINGS', route: '/more', anchor: 'more-co-share',
    say: 'Co-share is when another CID agent helps you.',
    do: 'Same company. They split your half — not the same as co-agency.',
  },
  {
    id: 'earnings-statement',
    chapter: 'FINDING THINGS', route: '/earnings', anchor: 'earnings-statement-btn',
    say: 'Your statement shows every ringgit, whenever you want it.',
    do: 'It is made fresh each time — there is nothing to wait for.',
  },
];

export const LESSONS = [
  {
    id: 'spine',
    name: 'The whole deal',
    sub: 'offer → signatures → sale → money',
    blurb: 'Write a booking form, get it signed, issue it, and follow the money.',
    mins: 10,
    steps: SPINE,
  },
  {
    id: 'stages',
    name: 'When to change the stage',
    sub: 'the six you can set',
    blurb: 'What each stage means and when to use it.',
    mins: 3,
    steps: STAGES,
  },
  {
    id: 'authchain',
    name: 'The owner’s letter',
    sub: 'letter → listing → offer',
    blurb: 'How the ALS feeds the listing, and the listing feeds the buyer’s offer.',
    mins: 4,
    steps: CHAIN,
  },
  {
    id: 'coshare',
    name: 'Sharing a deal',
    sub: 'another agent brings the buyer',
    blurb: 'Accept a co-share, raise the sale together, sign the split, and get paid.',
    mins: 6,
    steps: COSHARE,
  },
  {
    id: 'around',
    name: 'Finding your way around',
    sub: 'what lives where',
    blurb: 'A quick tour of the menus, and co-share vs co-agency.',
    mins: 2,
    steps: AROUND,
  },
];

export const lessonById = (id) => LESSONS.find((l) => l.id === id) || null;

/**
 * The steps of a lesson as ACTUALLY walked — on this build, once the fork has
 * been answered.
 *
 * TWO filters, in this order. First the BUILD: the lessons above are authored
 * against the phone, and the desktop app disagrees with them in a few dozen
 * places — a sidebar instead of a tab bar and a More hub, one page instead of
 * three tabs, a document pane instead of a document button. Every one of those
 * differences lives in tourDesktop.js; `toDesktop` folds it in, dropping the
 * steps that have nothing to point at here and rewriting the verbs of the rest
 * for a mouse.
 *
 * Then the BRANCH: a branch step belongs to one route through the lesson; an
 * untagged step is on both. Before the fork is answered `path` is null and only
 * the shared steps survive — which is correct, because every branch step sits
 * after the fork.
 *
 * Everything that indexes into a lesson (next, back, resume, the progress bar)
 * must use THIS list, or the numbering drifts from what the agent is looking at
 * — which on desktop would be off by however many navigation steps the sidebar
 * made unnecessary.
 */
export const stepsFor = (lesson, path, build) => {
  const authored = lesson ? lesson.steps : [];
  const walked = build === 'desktop' ? toDesktop(authored) : authored;
  return walked.filter((s) => !s.path || s.path === path);
};

